/* ── WORK_TRACK — Chat (direct messages) ──────────────────── */

import {
  $,
  $$,
  esc,
  icon,
  state,
  chatState,
  openModal,
  closeModal,
  avatarHtml,
  activeMembers,
  registerView,
  go,
  api,
  toast
} from './core';
import { setChatPendingImage, sendComposerMessage, setupChatTextarea } from './composer';
import { highlightMentions, attachMentionAutocomplete, setChatMentionHandler } from './mentions';
import type { ChatMessage, ChatWSInbound, Member } from './types';

setChatMentionHandler(channel => {
  if (String(channel).startsWith('meeting:')) {
    go('meet');
    return;
  }
  go('chat');
  connectChat(String(channel));
});

// ── Unread DM tracking (badges) ───────────────────────────
let unreadMap = new Map<string, number>();
let unreadTotal = 0;

function unreadFor(ch: string): number {
  return unreadMap.get(ch) || 0;
}

function markChatRead(ch: string): void {
  if (!ch || !ch.startsWith('dm:')) return;
  const had = unreadFor(ch) > 0;
  unreadMap.set(ch, 0);
  unreadTotal = [...unreadMap.values()].reduce((s, n) => s + n, 0);
  if (had) {
    renderNavBadge();
    renderChatList();
  }
  api('/api/chat/read', 'POST', { channel: ch }).catch(() => {
    /* best-effort */
  });
}

function renderNavBadge(): void {
  const b = $('#chatNavBadge');
  if (!b) return;
  if (unreadTotal === 0) {
    b.classList.add('hidden');
    b.textContent = '';
    b.classList.remove('dot');
    return;
  }
  b.classList.remove('hidden');
  if (unreadTotal === 1) {
    b.textContent = '';
    b.classList.add('dot');
  } else {
    b.textContent = unreadTotal > 99 ? '99+' : String(unreadTotal);
    b.classList.remove('dot');
  }
}

function dmBadgeHtml(ch: string | null): string {
  if (!ch) return '';
  const unread = unreadFor(ch);
  if (unread <= 0) return '';
  return `<span class="dm-badge${unread === 1 ? ' dot' : ''}">${unread === 1 ? '' : unread}</span>`;
}

export async function refreshUnreadChats(): Promise<void> {
  if (!state.token || !state.user) return;
  try {
    const d = await api<{ total: number; channels: { channel: string; unread: number }[] }>('/api/chat/unread');
    unreadMap = new Map((d.channels || []).map(c => [c.channel, c.unread]));
    unreadTotal = Number(d.total) || 0;
    renderNavBadge();
    renderChatList();
  } catch {
    /* offline */
  }
}

function dmChannelFor(userId: number): string | null {
  const a = state.user ? state.user.id : null;
  const b = Number(userId);
  if (!a || !b || a === b) return null;
  return `dm:${Math.min(a, b)}-${Math.max(a, b)}`;
}

function dmPeerId(ch: string): number | null {
  if (!ch || !ch.startsWith('dm:')) return null;
  const me = Number(state.user ? state.user.id : 0);
  const ids = ch.slice(3).split('-').map(Number);
  return ids.find(id => id && id !== me) || null;
}

function dmPeer(ch: string): Member | null {
  const pid = dmPeerId(ch);
  if (!pid) return null;
  return (state.members || []).find(x => x.id === pid) || null;
}

function dmPeerName(ch: string): string {
  const m = dmPeer(ch);
  if (!m) return dmPeerId(ch) ? 'Member ' + dmPeerId(ch) : 'Direct message';
  return m.name || m.username || 'Member';
}

function dmChannels(): string[] {
  let arr: unknown = [];
  try {
    arr = JSON.parse(localStorage.getItem('wt_dms') || '[]');
  } catch {
    arr = [];
  }
  return Array.isArray(arr) ? (arr as string[]).filter(ch => typeof ch === 'string' && /^dm:\d+-\d+$/.test(ch)) : [];
}

function addDmChannel(ch: string): void {
  if (!ch || !ch.startsWith('dm:')) return;
  const arr = dmChannels();
  if (!arr.includes(ch)) {
    arr.push(ch);
    localStorage.setItem('wt_dms', JSON.stringify(arr));
  }
}

export async function loadChat(): Promise<void> {
  if (!chatState.channel) {
    const last = localStorage.getItem('wt_last_channel') || '';
    if (/^dm:\d+-\d+$/.test(last)) chatState.channel = last;
  }
  renderChatList();
  if (chatState.channel && (!chatState.ws || chatState.ws.readyState !== WebSocket.OPEN)) {
    connectChat(chatState.channel);
  } else if (!chatState.channel) {
    const msgs = $('#chatMessages');
    if (msgs) msgs.innerHTML = '<div class="chat-empty">Search for a person above to start chatting.</div>';
  }
  refreshUnreadChats();
}

function matchingMembers(q: string): Member[] {
  return activeMembers()
    .filter(m => m.id !== state.user?.id)
    .filter(m => {
      if (!q) return true;
      const n = ((m.name || '') + ' ' + (m.username || '')).toLowerCase();
      return n.includes(q);
    });
}

function memberBtn(m: Member): string {
  const n = m.name || m.username;
  const ch = dmChannelFor(m.id);
  const active = ch === chatState.channel;
  return `<button class="chat-channel dm ${active ? 'active' : ''}" data-dm-target="${m.id}">
    ${avatarHtml(n.slice(0, 2).toUpperCase(), m.id, m.avatar || null)}
    <span class="dm-name">${esc(n)}</span>
    ${dmBadgeHtml(ch)}
  </button>`;
}

function bindMemberClick(scope: string): void {
  $$<HTMLButtonElement>(`${scope} [data-dm-target]`).forEach(btn =>
    btn.addEventListener('click', () => {
      const ch = dmChannelFor(Number(btn.dataset.dmTarget));
      if (!ch) return;
      openDm(ch);
    })
  );
}

function renderChatList(): void {
  const el = $('#chatChatList');
  if (!el || !state.user) return;
  let dms = dmChannels();
  if (chatState.channel.startsWith('dm:') && !dms.includes(chatState.channel)) dms = dms.concat(chatState.channel);
  const membersLoaded = state.members.length > 0;
  if (membersLoaded) {
    const stale = dms.filter(ch => !dmPeer(ch));
    dms = dms.filter(ch => dmPeer(ch));
    if (stale.length) {
      const fresh = dmChannels().filter(ch => !stale.includes(ch));
      localStorage.setItem('wt_dms', JSON.stringify(fresh));
    }
    // Pull in DMs that have unread messages even if the user never opened
    // them before, so the person who texted them shows up with a badge.
    unreadMap.forEach((count, ch) => {
      if (count > 0 && !dms.includes(ch) && dmPeer(ch)) dms = dms.concat(ch);
    });
    localStorage.setItem('wt_dms', JSON.stringify(dms.filter(ch => /^dm:\d+-\d+$/.test(ch))));
  }
  if (!dms.length) {
    el.innerHTML = membersLoaded
      ? '<div class="chat-empty small">No active chats yet. Search for a person above or use the People button.</div>'
      : '<div class="chat-empty small">No chats yet. Search for a person above or use the People button.</div>';
    return;
  }
  el.innerHTML = dms
    .map(ch => {
      const active = ch === chatState.channel;
      return `<button class="chat-channel dm ${active ? 'active' : ''}" data-ch="${esc(ch)}">
      ${avatarHtml(dmPeerName(ch).slice(0, 2).toUpperCase(), dmPeerId(ch) || 0, (dmPeer(ch) || {}).avatar || null)}
      <span class="dm-name">${esc(dmPeerName(ch))}</span>
      ${dmBadgeHtml(ch)}
    </button>`;
    })
    .join('');
  $$<HTMLButtonElement>('#chatChatList [data-ch]').forEach(btn =>
    btn.addEventListener('click', () => connectChat(btn.dataset.ch || ''))
  );
}

function renderUserResults(): void {
  const wrap = $<HTMLInputElement>('#dmSearch');
  const el = $('#dmResultList');
  if (!el || !state.user) return;
  const q = (wrap ? wrap.value : '').trim().toLowerCase();
  if (!q) {
    hideUserResults();
    return;
  }
  const members = matchingMembers(q);
  if (!members.length) {
    el.innerHTML = '<div class="chat-empty small">No users match.</div>';
    return;
  }
  el.innerHTML = members.map(memberBtn).join('');
  bindMemberClick('#dmResultList');
}

function hideUserResults(): void {
  const el = $('#dmResultList');
  if (el) el.innerHTML = '';
}

function openDm(ch: string): void {
  if (!ch) return;
  addDmChannel(ch);
  connectChat(ch);
  const inp = $<HTMLInputElement>('#dmSearch');
  if (inp) inp.value = '';
  hideUserResults();
  closeModal();
}

function openPeopleModal(): void {
  const members = matchingMembers('');
  const rows = members.length
    ? members
        .map(m => {
          const n = m.name || m.username;
          const ch = dmChannelFor(m.id);
          return `<button class="people-row" data-dm-target="${m.id}">
        ${avatarHtml(n.slice(0, 2).toUpperCase(), m.id, m.avatar || null)}
        <span class="people-name">${esc(n)}</span>
        <span class="people-user">@${esc(m.username || '')}</span>
        ${dmBadgeHtml(ch)}
        <span class="people-msg">${icon('chat', 15)} Message</span>
      </button>`;
        })
        .join('')
    : '<div class="chat-empty small">No other members yet.</div>';
  openModal(`
    <div class="modal-head"><h2>People</h2><button type="button" class="modal-close">${icon('x', 16)}</button></div>
    <div class="people-list">${rows}</div>`);
  bindMemberClick('#modalCard');
  const closeBtn = $('#modalCard .modal-close');
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
}

export function connectChat(channel: string): void {
  if (!channel) return;
  chatState.channel = channel;
  localStorage.setItem('wt_last_channel', channel);
  if (channel.startsWith('dm:')) addDmChannel(channel);
  markChatRead(channel);
  const headName = $('#chatHeadName');
  if (headName)
    headName.innerHTML = `${avatarHtml(dmPeerName(channel).slice(0, 2).toUpperCase(), dmPeerId(channel) || 0, (dmPeer(channel) || {}).avatar || null)} <strong>${esc(dmPeerName(channel))}</strong>`;
  renderChatList();
  updateChatClearBtn();
  hideUserResults();
  setChatPendingImage(null);
  chatState.explicitClose = false;
  if (chatState.reconnectTimer) clearTimeout(chatState.reconnectTimer);
  if (chatState.ws) {
    chatState.ws.onclose = null;
    try {
      chatState.ws.close();
    } catch {
      /* already closed */
    }
    chatState.ws = null;
  }

  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const url = `${proto}://${location.host}/api/chat?token=${encodeURIComponent(state.token || '')}&channel=${encodeURIComponent(channel)}`;
  setChatStatus('connecting');
  try {
    chatState.ws = new WebSocket(url);
  } catch {
    scheduleChatReconnect();
    return;
  }

  chatState.ws.onopen = () => {
    setChatStatus('online', true);
    $('#chatMessages').innerHTML = '';
  };
  chatState.ws.onmessage = ev => {
    let msg: ChatWSInbound;
    try {
      msg = JSON.parse(ev.data as string);
    } catch {
      return;
    }
    if (msg.type === 'history') {
      $('#chatMessages').innerHTML =
        (msg.messages || []).map(renderChatMsg).join('') || '<div class="chat-empty">No messages yet.</div>';
      scrollChat();
      markChatRead(chatState.channel);
    } else if (msg.type === 'message') {
      const cont = $('#chatMessages');
      if (cont.querySelector('.chat-empty')) cont.innerHTML = '';
      cont.insertAdjacentHTML('beforeend', renderChatMsg(msg));
      scrollChat();
      markChatRead(chatState.channel);
    } else if (msg.type === 'presence') {
      setChatOnline(msg.count);
    } else if (msg.type === 'cleared') {
      $('#chatMessages').innerHTML = '<div class="chat-empty">Chat cleared.</div>';
      scrollChat();
    }
  };
  chatState.ws.onclose = () => {
    chatState.ws = null;
    if (chatState.explicitClose) {
      setChatStatus('offline');
      return;
    }
    setChatStatus('offline', false, 'Reconnecting…');
    scheduleChatReconnect();
  };
  chatState.ws.onerror = () => {
    /* onclose handles retry */
  };
}

function scheduleChatReconnect(): void {
  if (chatState.reconnectTimer) clearTimeout(chatState.reconnectTimer);
  chatState.reconnectTimer = window.setTimeout(() => {
    if (state.view === 'chat') connectChat(chatState.channel);
  }, 3000);
}

function formatDocSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function docCardHtml(bodyText: string): string {
  let href = bodyText;
  let fname = 'Document';
  let fsize = '';
  try {
    const d = JSON.parse(bodyText) as { n?: unknown; s?: unknown; d?: unknown; u?: unknown };
    const url = typeof d.u === 'string' && d.u ? d.u : typeof d.d === 'string' && d.d ? d.d : '';
    if (d && url) {
      href = url;
      if (typeof d.n === 'string' && d.n) fname = d.n;
      if (typeof d.s === 'number') fsize = formatDocSize(d.s);
    } else {
      fname = decodeURIComponent((href.split('/').pop() || '').split('?')[0]) || 'Document';
    }
  } catch {
    fname = decodeURIComponent((href.split('/').pop() || '').split('?')[0]) || 'Document';
  }
  const ext = (fname.includes('.') ? fname.slice(fname.lastIndexOf('.') + 1) : '').slice(0, 5).toUpperCase();
  return `<a class="chat-doc" href="${esc(href)}" download="${esc(fname)}">
    <span class="chat-doc-icon">${ext ? esc(ext) : icon('fileText', 20)}</span>
    <span class="chat-doc-meta"><strong>${esc(fname)}</strong>${fsize ? `<span>${esc(fsize)}</span>` : ''}</span>
    <span class="chat-doc-dl">${icon('download', 16)}</span>
  </a>`;
}

export function renderChatMsg(m: ChatMessage): string {
  const mine = state.user && m.user_id === state.user.id;
  const member = (state.members || []).find(x => x.id === m.user_id);
  const selfName = state.user ? state.user.name || state.user.username || '' : '';
  const peerName = member ? member.name || member.username || '' : '';
  const rawName = m.user_name && m.user_name !== 'Unknown' ? m.user_name : '';
  const senderName = rawName || (mine ? selfName : peerName);
  const name = esc(senderName || 'Unknown');
  const time = new Date(m.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const kind = m.kind || 'text';
  const bodyText = m.body == null ? '' : String(m.body);
  const initials = (senderName || '?').slice(0, 2).toUpperCase();
  const avatar = avatarHtml(
    initials,
    Number(m.user_id) || (mine && state.user ? state.user.id : 0),
    m.user_avatar || (mine && state.user ? state.user.avatar : null) || (member ? member.avatar || null : null)
  );
  let bodyHtml: string;
  if (kind === 'image') {
    bodyHtml = `<img class="chat-media" src="${esc(bodyText)}" alt="Shared photo">`;
  } else if (kind === 'sticker') {
    bodyHtml = `<span class="chat-sticker" title="${esc(name)}">${esc(bodyText)}</span>`;
  } else if (kind === 'gif') {
    bodyHtml = `<img class="chat-media chat-gif" src="${esc(bodyText)}" alt="GIF">`;
  } else if (kind === 'emoji') {
    bodyHtml = `<span class="chat-emoji" title="${esc(bodyText)}">${esc(bodyText)}</span>`;
  } else if (kind === 'document') {
    bodyHtml = docCardHtml(bodyText);
  } else {
    bodyHtml = highlightMentions(esc(bodyText));
  }
  return `
    <div class="chat-msg ${mine ? 'mine' : ''}">
      <div class="chat-msg-avatar">${avatar}</div>
      <div class="chat-msg-main">
        <div class="chat-msg-head">${mine ? '<strong>You</strong>' : `<strong>${name}</strong>`}<span>${time}</span></div>
        <div class="chat-msg-body">${bodyHtml}</div>
      </div>
    </div>`;
}

function setChatStatus(label: string, isOnline?: boolean, sub?: string): void {
  const el = $('#chatOnline');
  el.classList.toggle('online', !!isOnline);
  el.textContent = sub ? `${label} — ${sub}` : label;
}

function setChatOnline(count: number): void {
  const el = $('#chatOnline');
  el.classList.add('online');
  el.textContent = `${count} online`;
}

function scrollChat(): void {
  const el = $('#chatMessages');
  el.scrollTop = el.scrollHeight;
}

const chatFormEl = $<HTMLFormElement>('#chatForm');
chatFormEl.addEventListener('submit', e => {
  e.preventDefault();
  sendComposerMessage('chat');
});

const chatInputEl = $<HTMLTextAreaElement>('#chatInput');
if (chatInputEl) {
  attachMentionAutocomplete(chatInputEl, () => state.members);
  setupChatTextarea(chatInputEl, chatFormEl);
}

export function closeChat(): void {
  chatState.explicitClose = true;
  if (chatState.reconnectTimer) clearTimeout(chatState.reconnectTimer);
  setChatPendingImage(null);
  if (chatState.ws) {
    chatState.ws.onclose = null;
    try {
      chatState.ws.close();
    } catch {
      /* already closed */
    }
    chatState.ws = null;
  }
}

// ── DM search + people wiring ─────────────────────────────
const dmSearchEl = $<HTMLInputElement>('#dmSearch');
if (dmSearchEl) {
  dmSearchEl.addEventListener('input', renderUserResults);
  dmSearchEl.addEventListener('focus', () => {
    if (dmSearchEl.value.trim()) renderUserResults();
  });
}
const peopleBtn = $('#peopleBtn');
if (peopleBtn) peopleBtn.addEventListener('click', openPeopleModal);

function updateChatClearBtn(): void {
  const btn = $('#chatMoreBtn');
  if (!btn) return;
  const ch = chatState.channel;
  const isDm = !!ch && ch.startsWith('dm:');
  const isAdmin = !!state.user && (state.user.role === 'owner' || state.user.role === 'admin');
  btn.classList.toggle('hidden', !(ch && (isDm || isAdmin)));
}

function hideChatMoreMenu(): void {
  const menu = $('#chatMoreMenu');
  const btn = $('#chatMoreBtn');
  if (menu) menu.classList.add('hidden');
  if (btn) btn.setAttribute('aria-expanded', 'false');
}

const chatMoreBtn = $('#chatMoreBtn');
if (chatMoreBtn) {
  chatMoreBtn.addEventListener('click', e => {
    e.stopPropagation();
    const menu = $('#chatMoreMenu');
    if (!menu) return;
    const open = menu.classList.toggle('hidden');
    chatMoreBtn.setAttribute('aria-expanded', String(!open));
  });
}

const chatClearBtn = $('#chatClearBtn');
if (chatClearBtn) {
  chatClearBtn.addEventListener('click', async () => {
    const ch = chatState.channel;
    hideChatMoreMenu();
    if (!ch) return;
    if (!confirm('Clear all messages in this chat? This cannot be undone.')) return;
    try {
      await api<{ ok: boolean }>('/api/chat/clear', 'POST', { channel: ch });
      $('#chatMessages').innerHTML = '<div class="chat-empty">Chat cleared.</div>';
      toast('Chat cleared');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to clear chat', true);
    }
  });
}

document.addEventListener('click', e => {
  if (!(e.target as HTMLElement).closest('.chat-more-wrap')) hideChatMoreMenu();
});
document.addEventListener('click', e => {
  const res = $('#dmResultList');
  if (res && res.innerHTML && !(e.target as HTMLElement).closest('.chat-search-wrap')) hideUserResults();
});

registerView('chat', loadChat);

// Poll for new unread DMs on every screen while logged in so the Chat
// nav icon and channel list stay in sync even when a message arrives
// in a channel we're not currently viewing.
window.setInterval(refreshUnreadChats, 10000);

// ── Live unread push (no reload needed) ─────────────────────
// A one-way WebSocket per user (`notif:<uid>`) that receives a
// `dm-unread` ping the instant someone DMs them, so the Chat nav
// badge updates immediately on every screen. The 10s poll above is
// the offline/fallback path.
let notifWs: WebSocket | null = null;

function connectNotifSocket(): void {
  if (notifWs || !state.token || !state.user) return;
  const uid = state.user.id;
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const url = `${proto}://${location.host}/api/chat?token=${encodeURIComponent(state.token)}&channel=notif:${uid}`;
  let ws: WebSocket;
  try {
    ws = new WebSocket(url);
  } catch {
    return;
  }
  notifWs = ws;
  ws.onmessage = ev => {
    let m: { type?: string; channel?: string } | null = null;
    try {
      m = JSON.parse(ev.data as string);
    } catch {
      return;
    }
    if (m && m.type === 'dm-unread') refreshUnreadChats();
  };
  ws.onclose = () => {
    notifWs = null;
    setTimeout(connectNotifSocket, 5000);
  };
  ws.onerror = () => {
    try {
      ws.close();
    } catch {
      /* ignore */
    }
  };
}

// Open the socket as soon as we're logged in (and reconnect if it drops).
window.setInterval(() => {
  if (!notifWs && state.token && state.user) connectNotifSocket();
}, 5000);
